import { createSlice } from "@reduxjs/toolkit";
import { CompetitionAnalysisState, defaultCompetitionContentFilters } from "types/competition-analysis";
import { fetchCompeitionGroupThunk, fetchCompetitionAnalysisGroupsThunk, fetchCompetitionContentThunk, fetchCompetitionReportShareIdThunk, fetchCompetitorsThunk, fetchSharedCompeitionGroupThunk } from "./thunk";
import persistReducer from "redux-persist/es/persistReducer";
import storage from 'redux-persist/lib/storage';
import { ERROR } from "types";
const initialState: CompetitionAnalysisState = {
    groups: {
        loading: true,
        error: null,
        items: [],
        count: 0,
        last_fetched_at: 0,
    },
    loading: true,
    error: null,
    group: {},
    competitors: {
        loading: true,
        error: null,
        items: [],
        count: 0,
        last_fetched_at: 0,
    },
    shares: {
        loading: true,
        error: null,
        data: {}
    },
    shared_group: {},
    content: {
        applied_filter_id: null,
        loading: true,
        error: null,
        filtered_lists: {},
        total: 0,
        filters: defaultCompetitionContentFilters,
    }
}

const state = createSlice({
    name: 'competitionAnalysis',
    initialState,
    reducers: {
        markGroupDataExpired: (state, { payload: groupId }) => {
            if (state.group[groupId]) {
                state.group[groupId].last_fetched_at = 0;
            }
        },
        markCompetitorsDataExpired: (state) => {
            state.competitors.last_fetched_at = 0;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCompetitionAnalysisGroupsThunk.pending, (state) => {
                state.groups.loading = true;
                state.groups.error = null;
            })
            .addCase(fetchCompetitionAnalysisGroupsThunk.fulfilled, (state, { payload }) => {
                const { items, count } = payload;
                state.groups.loading = false;
                state.groups.error = null;
                state.groups.items = items;
                state.groups.count = count;
                state.groups.last_fetched_at = Date.now()
            })
            .addCase(fetchCompetitionAnalysisGroupsThunk.rejected, (state) => {
                state.groups.loading = false;
                state.groups.error = "Failed to fetch!";
            })
            .addCase(fetchCompeitionGroupThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCompeitionGroupThunk.fulfilled, (state, { payload }) => {
                const { creator_follower_tiers, competition_group, competitionGroupId } = payload;
                state.loading = false;
                state.error = null;
                state.group[competitionGroupId] = {
                    data: competition_group,
                    creator_follower_tiers: creator_follower_tiers,
                    last_fetched_at: Date.now()
                }
            })
            .addCase(fetchCompeitionGroupThunk.rejected, (state) => {
                state.loading = false;
                state.error = "Failed to fetch!";
            })
            .addCase(fetchCompetitorsThunk.pending, (state) => {
                state.competitors.loading = true;
                state.competitors.error = null;
            })
            .addCase(fetchCompetitorsThunk.fulfilled, (state, { payload }) => {
                const { items, count } = payload;
                state.competitors.loading = false;
                state.competitors.error = null;
                state.competitors.items = items;
                state.competitors.count = count;
                state.competitors.last_fetched_at = Date.now()
            })
            .addCase(fetchCompetitorsThunk.rejected, (state) => {
                state.competitors.loading = false;
                state.competitors.error = "Failed to fetch!";
            })
            .addCase(fetchCompetitionReportShareIdThunk.pending, (state) => {
                state.shares.loading = true;
                state.shares.error = null;
            })
            .addCase(fetchCompetitionReportShareIdThunk.fulfilled, (state, { payload }) => {
                const { uuid, competitionGroupId } = payload;
                state.shares.loading = false;
                state.shares.error = null;
                state.shares.data = {
                    ...state.shares.data,
                    [competitionGroupId]: {
                        uuid,
                        last_fetched_at: Date.now()
                    }
                }
            })
            .addCase(fetchCompetitionReportShareIdThunk.rejected, (state, { payload }) => {
                // state.shares.error = 'Failed to generate share ID';
                state.shares.loading = false;
            })
            .addCase(fetchSharedCompeitionGroupThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSharedCompeitionGroupThunk.fulfilled, (state, { payload }) => {
                const { creator_follower_tiers, competition_group, competitionGroupShareId } = payload;
                state.loading = false;
                state.error = null;
                state.shared_group[competitionGroupShareId] = {
                    data: competition_group,
                    creator_follower_tiers: creator_follower_tiers,
                    last_fetched_at: Date.now()
                }
            })
            .addCase(fetchSharedCompeitionGroupThunk.rejected, (state) => {
                state.loading = false;
                state.error = "Failed to fetch!";
            })
            .addCase(fetchCompetitionContentThunk.pending, (state) => {
                state.content.loading = true;
                state.content.error = null;
            })
            .addCase(fetchCompetitionContentThunk.fulfilled, (state, action) => {
                state.content.loading = false;
                state.content.error = null;
                const filterId = action.payload.filterId;
                const existingData = state.content.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.content = {
                    ...state.content,
                    filtered_lists: {
                        ...(state.content?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.count,
                            error: null,
                            loading: false
                        }
                    },
                    total: action.payload.count
                }
                state.content.applied_filter_id = filterId;
            })
            .addCase(fetchCompetitionContentThunk.rejected, (state, action) => {
                state.content.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.content.error = action.payload || "Failed to fetch applications"
            })

    }
})

export const { markGroupDataExpired, markCompetitorsDataExpired } = state.actions;

export default persistReducer({
    key: 'competition',
    storage,
    whitelist: ['shares']
}, state.reducer)
